import React from 'react';
import PropTypes from 'prop-types';
import { SignUp } from 'aws-amplify-react';
import { withRouter } from 'react-router-dom';
import { withCookies } from 'react-cookie';
import {
  Auth,
  JS,
} from 'aws-amplify';

import {
  ExtendedFlex,
  Heading,
  Body,
  Data,
  TooltipIcon,
  TooltipLink,
  Tooltip,
  InlineTextLink,
  Image,
  ScrollToTopOnMount,
} from '../../coreui';

import Container from './Container';
import FirstContainer from './FirstContainer';
import SecondContainer from './SecondContainer';
import InfoBlock from './InfoBlock';
import UnorderedList from './UnorderedList';
import ListItem from './ListItem';
import pressMentionsData from '../../homepage/pressMentions/pressMentionsData';
import logoMotleyText from '../../assets/press_logo_motleytext.png';

import Form from '../../components/forms/Form';
import Actions from '../../components/forms/Actions';
import Label from '../../components/forms/Label';
import Input from '../../components/forms/Input';
import PasswordInput from '../../components/forms/PasswordInput';
import PhoneInput from '../../components/forms/PhoneInput';
import RadioGroup from '../../components/forms/RadioGroup';
import RadioLabel from '../../components/forms/RadioLabel';
import CheckboxLabel from '../../components/forms/CheckboxLabel';
import PrimaryButton from '../../components/forms/PrimaryButton';
import SecondaryButton from '../../components/forms/SecondaryButton';

import StepCounter from '../../components/StepCounter';
import trackError from './trackError';

pressMentionsData[1].logoImage = logoMotleyText;
const orderedPressMentionsData = pressMentionsData.sort((a, b) => {
  if (a.weight === b.weight) {
    return 0;
  }
  return a.weight < b.weight ? -1 : 1;
});

class CustomSignUp extends SignUp {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      submitting: false,
      accountPurpose: props.accountPurpose || 'investing',
      usCitizen: undefined,
      internationalPhone: false,
      sms: true,
      phoneValid: undefined,
    };
    this._validAuthStates = ['signUp'];

    this.phoneInput = null;
    this.setPhoneInputRef = (ref) => { this.phoneInput = ref; };

    this.checkContact = this.checkContact.bind(this);
    this.signIn = this.signIn.bind(this);
    this.updateState = this.updateState.bind(this);
    this.handleRadioChange = this.handleRadioChange.bind(this);
    this.handlePhoneInputChange = this.handlePhoneInputChange.bind(this);
    this.handleSelectFlag = this.handleSelectFlag.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);

    this.stepOne = this.stepOne.bind(this);
    this.stepTwo = this.stepTwo.bind(this);
  }

  static getDerivedStateFromProps(props, state) {
    if (props.authState !== 'signUp' && state.submitting) {
      return {
        submitting: false,
      };
    }
    return null;
  }

  handlePhoneInputChange(isValid, number, country) {
    this.setState({
      phoneValid: isValid,
    });
    this.inputs = this.inputs || {};
    // eslint-disable-next-line camelcase
    this.inputs.phone_line_number = number.replace(/[\s-()]/g, '');
    // eslint-disable-next-line camelcase
    this.inputs.dial_code = `+${country.dialCode}`;
  }

  handleSelectFlag(currentNumber, countryData) {
    const isUS = countryData.iso2 === 'us';

    this.updateState({
      internationalPhone: !isUS,
      usCitizen: !isUS ? 'no' : undefined,
    });
  }

  handleRadioChange(evt) {
    const { name, value, checked } = evt.target;

    const setValue = checked ? value : undefined;
    this.updateState({
      [name]: setValue,
    });

    if (name === 'accountType') {
      this.props.history.replace(this.props.location.pathname, {
        ...this.props.history.state,
        accountPurpose: setValue,
      });
    }
  }

  error(err) {
    this.updateState({ submitting: false });
    trackError('SignUp Error', { error: err });
    super.error(err);
  }

  updateState(...args) {
    this.setState(...args);
  }

  handleFormSubmit(e, callback) {
    e.preventDefault();
    if (!this.state.submitting) {
      const {
        location: {
          state: {
            step: stepNum = 1,
            email,
          } = {},
        },
      } = this.props;
      if (stepNum === 2) {
        if (this.state.phoneValid) {
          this.updateState({ submitting: true });
          callback();
        }
      } else {
        this.props.history.push(this.props.location.pathname, {
          email,
          accountPurpose: this.state.accountPurpose,
          step: 2,
        });
      }
    }
  }

  signUp() {
    if (!this.inputs.dial_code) {
      // eslint-disable-next-line camelcase
      this.inputs.dial_code = this.getDefaultDialCode();
    }
    const validation = this.validate();
    if (validation && validation.length > 0) {
      return this.error(`The following fields need to be filled out: ${validation.join(', ')}`);
    }
    if (!Auth || typeof Auth.signUp !== 'function') {
      throw new Error('No Auth module found, please ensure @aws-amplify/auth is imported');
    }

    const signupInfo = {
      username: this.inputs.username,
      password: this.inputs.password,
      attributes: { },
    };

    const inputKeys = Object.keys(this.inputs);
    const inputVals = Object.values(this.inputs);

    inputKeys.forEach((key, index) => {
      if (!['username', 'password', 'checkedValue', 'dial_code'].includes(key)) {
        if (key !== 'phone_line_number' && key !== 'dial_code' && key !== 'error') {
          const newKey = `${this.needPrefix(key) ? 'custom:' : ''}${key}`;
          signupInfo.attributes[newKey] = inputVals[index];
        } else if (inputVals[index]) {
          // eslint-disable-next-line camelcase
          signupInfo.attributes.phone_number = `${this.inputs.dial_code}${this.inputs.phone_line_number.replace(/[-()]/g, '')}`;
        }
      }
    });

    Auth.signUp(signupInfo).then(() => {
      this.signIn();
    })
      .catch((err) => this.error(err));

    return null;
  }

  async signIn() {
    const { username = '', password } = this.inputs;
    if (!Auth || typeof Auth.signIn !== 'function') {
      throw new Error('No Auth module found, please ensure @aws-amplify/auth is imported');
    }
    this.setState({ loading: true });
    try {
      const user = await Auth.signIn(username, password);
      this.props.userRegisterRequested({
        sms: this.state.sms,
      });
      if (user.challengeName === 'SMS_MFA' || user.challengeName === 'SOFTWARE_TOKEN_MFA') {
        this.changeState('confirmSignIn', user);
      } else if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        this.changeState('requireNewPassword', user);
      } else if (user.challengeName === 'MFA_SETUP') {
        this.changeState('TOTPSetup', user);
      } else {
        this.checkContact(user);
      }
    } catch (err) {
      if (err.code === 'UserNotConfirmedException') {
        this.changeState('confirmSignUp', { username });
      } else if (err.code === 'PasswordResetRequiredException') {
        this.changeState('forgotPassword', { username });
      } else {
        this.error(err);
      }
    } finally {
      this.setState({ loading: false });
    }
  }

  checkContact(user) {
    if (!Auth || typeof Auth.verifiedContact !== 'function') {
      throw new Error('No Auth module found, please ensure @aws-amplify/auth is imported');
    }
    Auth.verifiedContact(user)
      .then((data) => {
        if (!JS.isEmpty(data.verified)) {
          this.changeState('signedIn', user);
        } else {
          Auth.verifyCurrentUserAttribute('email')
            .then(() => {
              this.changeState('confirmSignUp', user.username);
            })
            .catch((err) => this.error(err));
        }
      });
  }

  stepOne() {
    const {
      location: {
        state: {
          accountPurpose = this.state.accountPurpose || 'investing',
        } = {},
      },
    } = this.props;

    return (
      <ExtendedFlex
        flexDirection="column"
      >
        <ScrollToTopOnMount />
        <Heading
          type="H2"
        >
          Get started by joining RealtyMogul now.
        </Heading>
        <Body
          type="B1"
        >
          Set up your account to unlock investment opportunities.
        </Body>
        <ExtendedFlex
          align="center"
        >
          <Heading
            type="H6"
            textTransform="uppercase"
          >
            Your Primary Reason For Joining
          </Heading>
          <TooltipIcon
            tooltipId="accountPurposeTooltip"
            ml={5}
          />
          <Tooltip id="accountPurposeTooltip">
            <ExtendedFlex
              flexDirection="column"
            >
              <Data type="D5">
                Personal Investing
              </Data>
              <Body type="B3">
                Choose this if you want to invest with an individual, joint, or
                retirement account, or as a legal entity such as an LLC, trust,
                or corporation.
              </Body>
              <Data type="D5">
                Raising Capital as a Real Estate Company
              </Data>
              <Body type="B3">
                Choose this if you are a real estate company looking to raise
                capital.
              </Body>
            </ExtendedFlex>
          </Tooltip>
        </ExtendedFlex>
        <RadioGroup
          name="accountPurpose"
          value={accountPurpose}
          onChange={this.handleRadioChange}
          inputProps={{
            'data-test': 'account_purpose',
            required: true,
          }}
        >
          <RadioLabel
            value="investing"
            label="Personal Investing"
          />
          <RadioLabel
            value="financing"
            label="Raising Capital as a Real Estate Company"
          />
        </RadioGroup>
      </ExtendedFlex>
    );
  }

  stepTwo() {
    const {
      location: {
        state: {
          accountPurpose,
        } = {},
      },
    } = this.props;
    const email = this.props.cookies.get('rm_captured_email');

    const symbols = '\\^$*.\\[\\]{}()?\\-"!@#%&\\/\\\\,><\':;\\|_~`';

    return (
      <ExtendedFlex
        flexDirection="column"
      >
        <ScrollToTopOnMount />
        {
          accountPurpose === 'investing' &&
          <Heading
            type="H2"
          >
            Set up your personal investing account.
          </Heading>
        }
        {
          accountPurpose === 'financing' &&
          <Heading
            type="H2"
          >
            Set up your account for raising capital.
          </Heading>
        }
        <Body
          type="B1"
        >
          Provide your account details below.
        </Body>
        <Label
          htmlFor="firstName"
        >
          First Name
          <Input
            id="firstName"
            placeholder="First Name"
            name="given_name"
            onChange={(e) => {
              const { value } = e.target;
              e.target.value = value.charAt(0).toUpperCase() + value.slice(1);
              this.handleInputChange(e);
            }}
            data-test="first_name"
            maxLength={40}
            required
          />
        </Label>
        <Label
          htmlFor="lastName"
        >
          Last Name
          <Input
            id="lastName"
            placeholder="Last Name"
            name="family_name"
            onChange={this.handleInputChange}
            data-test="last_name"
            maxLength={80}
            required
          />
        </Label>
        <Label
          htmlFor="username"
        >
          E-mail
          <Input
            id="username"
            placeholder="E-mail"
            name="username"
            type="email"
            autoComplete="email"
            defaultValue={email && email.toLowerCase()}
            onChange={(e) => {
              e.target.value = (e.target.value || '').toLowerCase();
              this.handleInputChange(e);
            }}
            data-test="email"
            required
          />
        </Label>
        <Label
          htmlFor="phone"
        >
          Phone
          <PhoneInput
            setPhoneInputRef={this.setPhoneInputRef}
            id="phone"
            name="phone_number"
            autoComplete="tel"
            onChange={this.handlePhoneInputChange}
            onSelectFlag={this.handleSelectFlag}
            data-test="phone"
            inputProps={{
              required: true,
              'data-test': 'phone',
            }}
          />
          {
            this.state.phoneValid === false &&
            <Data
              type="D4"
              display="block"
              my={2}
              color="red"
            >
              Please enter a valid phone number.
            </Data>
          }
          <Body
            type="B5"
            mt={10}
            mb={30}
            display="block"
            color="gray.7"
          >
            Please provide a valid phone number.
          </Body>
        </Label>
        {
          this.state.internationalPhone &&
          <>
            <Heading
              type="H6"
              display="flex"
              align="center"
              textTransform="uppercase"
            >
              Citizenship
              <TooltipIcon
                tooltipId="internationalTooltip"
                ml={5}
              />
            </Heading>
            <Tooltip id="internationalTooltip">
              <Body type="B3">
                US regulations limit investments from foreign investors. If
                you&apos;d like to learn more about your options, please
                complete the registration process.
              </Body>
            </Tooltip>
            <Body
              type="B2"
              mt={0}
              mb={30}
            >
              Are you a US Citizen or Permanent Resident?
            </Body>
            <RadioGroup
              name="usCitizen"
              onChange={this.handleRadioChange}
              defaultValue="no"
              inputProps={{
                'data-test': 'us_citizen',
                required: true,
              }}
            >
              <RadioLabel
                value="no"
                label="No"
              />
              <RadioLabel
                value="yes"
                label="Yes"
              />
            </RadioGroup>
          </>
        }
        <Label
          htmlFor="password"
        >
          Password
          <PasswordInput
            id="password"
            name="password"
            onChange={this.handleInputChange}
            autoComplete="new-password"
            placeholder="******************"
            data-test="password"
            pattern={`^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[${symbols}])[A-Za-z\\d${symbols}]{8,}$`}
            required
          />
          <Body
            type="B5"
            mt={10}
            mb={30}
            display="block"
            color="gray.7"
          >
            Password should be at least 8 characters, contain an uppercase
            character, a lowercase character and a symbol.
          </Body>
        </Label>
        <ExtendedFlex
          my={50}
          flexDirection="column"
        >
          <CheckboxLabel
            label={(
              <Body
                type="B1"
                display="block"
                mt={3}
                mb={0}
              >
                I have reviewed and agree to the <InlineTextLink display="inline" href="/terms-service" target="_blank">Terms of Service</InlineTextLink> and <InlineTextLink display="inline" href="/privacy-policy" target="_blank">Privacy Policy</InlineTextLink>.
              </Body>
            )}
            mb={50}
            inputProps={{
              'data-test': 'terms',
              required: true,
            }}
          />
          <CheckboxLabel
            label={(
              <Body
                type="B1"
                display="block"
                mt={3}
                mb={0}
              >
                I want to receive <TooltipLink component={<InlineTextLink />} tooltipId="smsTooltip">SMS messages</TooltipLink> (optional).
              </Body>
            )}
            checkboxProps={{
              checked: !!this.state.sms,
              onChange: (event) => {
                this.setState({
                  sms: event.target.checked,
                });
              },
            }}
            inputProps={{
              'data-test': 'commpref',
            }}
          />
        </ExtendedFlex>
        <Tooltip id="smsTooltip">
          <ExtendedFlex
            flexDirection="column"
          >
            <Data type="D5">
              SMS Consent (Optional)
            </Data>
            <Body type="B3">
              By checking this box, you are consenting to participate in
              RealtyMogul.com&apos;s SMS Communications Program with the phone
              number entered above. By participating, you consent to receive
              text messages sent by an automatic telephone dialing system and
              acknowledge that data usage charges may apply by your cell phone
              company.
            </Body>
            <Body type="B3">
              Messages will include notifications regarding your
              RealtyMogul.com account, investment opportunity updates, and
              occasional special offers and promotions from RealtyMogul.com.
              These can be configured at any time in your user profile.
            </Body>
          </ExtendedFlex>
        </Tooltip>
      </ExtendedFlex>
    );
  }

  showComponent() {
    const {
      history,
      location: {
        state: {
          accountPurpose,
          step: stepNum = 1,
        } = {},
      },
      products,
      steps,
    } = this.props;
    const email = this.props.cookies.get('rm_captured_email');
    const {
      usCitizen,
    } = this.state;

    if (this.checkCustomSignUpFields()) {
      this.signUpFields = this.props.signUpConfig.signUpFields;
    }

    // Set any defaults.
    // We need to pull this out of state because this.inputs gets reset by the
    // parent class when we're not the component being output.
    this.inputs = this.inputs || {};
    this.inputs['custom:account_purpose'] = accountPurpose;
    if (typeof usCitizen === 'undefined'
        && typeof this.inputs['custom:us_citizen'] !== 'undefined') {
      delete this.inputs['custom:us_citizen'];
    } else if (usCitizen) {
      this.inputs['custom:us_citizen'] = usCitizen;
    }

    // Make sure the default value email added to the inputs.
    if (email && !Object.prototype.hasOwnProperty.call(this.inputs, 'username')) {
      this.inputs.username = email.toLowerCase();
    }

    return (
      <ExtendedFlex
        flexDirection="column"
      >
        {
          (stepNum === 2 && accountPurpose === 'investing') &&
          <ExtendedFlex
            w="90%"
            maxWidth={1200}
            mt={50}
            mx="auto"
            display={['none', null, 'flex']}
          >
            <StepCounter
              steps={steps}
              activeStep={0}
            />
          </ExtendedFlex>
        }
        <Container>
          <FirstContainer
            mx={0}
            mb={0}
            pl={[null, null, 0]}
            boxSizing="content-box"
          >
            <Form onSubmit={(e) => this.handleFormSubmit(e, this.signUp)} >
              {
                stepNum === 1 &&
                this.stepOne()
              }{
                stepNum === 2 &&
                this.stepTwo()
              }
              <Actions
                flexWrap="wrap"
              >
                <PrimaryButton
                  loading={this.state.submitting}
                  disable={this.state.submitting}
                  buttonType="submit"
                >
                  Continue
                </PrimaryButton>
                {
                  stepNum === 1 &&
                  <ExtendedFlex
                    w={1}
                  >
                    <Body type="B2">Already have an account?</Body>
                    <SecondaryButton
                      type="L4"
                      onClick={() => this.changeState('signIn')}
                    >
                      Sign in
                    </SecondaryButton>
                  </ExtendedFlex>
                }
                {
                  stepNum > 1 &&
                  <SecondaryButton
                    type="L4"
                    onClick={() => history.goBack()}
                  >
                    Back
                  </SecondaryButton>
                }
              </Actions>
            </Form>
          </FirstContainer>
          <SecondContainer
            pl={60}
            borderLeft={1}
            borderColor="gray.10"
          >
            {
              (stepNum === 1 || accountPurpose === 'investing') &&
              <InfoBlock
                heading="Membership Benefits"
                headingProps={{
                  mt: 0,
                }}
              >
                <UnorderedList>
                  <ListItem>
                    24/7 Access to fully vetted investment opportunities.
                  </ListItem>
                  <ListItem>
                    A team of Investor Relations professionals are available to talk by
                    phone or email about using the RealtyMogul platform.
                  </ListItem>
                  <ListItem>
                    Access to exclusive, thoroughly vetted opportunities in
                    institutional-quality properties.
                  </ListItem>
                </UnorderedList>
              </InfoBlock>
            }
            {
              (stepNum === 2 && accountPurpose === 'financing') &&
              <InfoBlock
                heading="Designed for Efficiency"
                headingProps={{
                  mt: 0,
                }}
              >
                <UnorderedList>
                  <ListItem>
                    Gain access through our platform to a wide network of
                    institutional and retail investors previously difficult to reach.
                  </ListItem>
                  <ListItem>
                    Our ability to originate across the capital stack enables us
                    to be a flexible and creative capital provider for you and your business.
                  </ListItem>
                  <ListItem>
                    Our many investors are pooled into a single entity, and asset
                    management is handled internally, which allows us to behave as
                    a single high net-worth investor. That means one K1, one
                    distribution, one quarterly report.
                  </ListItem>
                </UnorderedList>
              </InfoBlock>
            }
            {
              (stepNum === 1 || accountPurpose === 'investing') &&
              <InfoBlock
                heading="As seen in"
                borderBottom="none"
              >
                {
                  orderedPressMentionsData.map((data) => (
                    <React.Fragment
                      key={data.name}
                    >
                      <Image
                        src={data.logoImage}
                        my={10}
                        maxWidth="140px"
                      />
                      <Data
                        type="D3"
                      >
                        {data.quote}
                      </Data>
                    </React.Fragment>
                  ))
                }
              </InfoBlock>
            }
            {
              (stepNum === 2 && accountPurpose === 'financing') &&
              <InfoBlock
                heading="Product Offerings"
                headingProps={{
                  mt: 60,
                  mb: 30,
                }}
                borderBottom="none"
              >

                {products.map((item) => (
                  <React.Fragment
                    key={item.label}
                  >
                    <Heading
                      type="H7"
                      m={0}
                    >
                      {item.label}
                    </Heading>
                    <Data
                      type="D3"
                      mb={25}
                    >
                      {item.value}
                    </Data>
                  </React.Fragment>
                ))}
              </InfoBlock>
            }
          </SecondContainer>
        </Container>
      </ExtendedFlex>
    );
  }
}

CustomSignUp.propTypes = {
  ...CustomSignUp.propTypes,
  history: PropTypes.object,
  location: PropTypes.object,
  accountPurpose: PropTypes.string,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  steps: PropTypes.array,
  userRegisterRequested: PropTypes.func,
};

export default withCookies(withRouter(CustomSignUp));
